import { useContext } from 'react'
import swal from 'sweetalert2'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faCheck, faCopy, faTimes, faTrashAlt,
} from '@fortawesome/pro-solid-svg-icons'

import {
  Button, Form, ModalContext, SidebarModalListItem, SidebarModal,
} from '@domain-group/suit-theme'

import { toggleArray } from '@domain-group/javascript-utils'

import { getTaskTypeSetting, subTaskTypes, taskTypes } from '@functions/taskListItem'
import { priorityKeys, prioritySettings } from '@functions/status'
import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import useCurrentUser from '@hooks/useCurrentUser'
import useTaskListItem from '@hooks/useTaskListItem'

import { TaskListItemModel, TaskListModel } from '@models/types'

import EntitySelector from '@components/EntitySelector'

import useCreateOrEditTaskListItemModal from './hooks/useCreateOrEditTaskListItemModal'

const MODAL_KEY = 'CreateOrEditTaskListItemModal'

const templateManagerRoleKeys = [
  'admin',
  'support',
  'brand_administrator',
  'provider_director',
  'provider_employee',
  'provider_contractor',
]

type UseTaskListItemPayload = ReturnType<typeof useTaskListItem>

type ModalCallbacks = {
  closeModal: () => void,
  createTaskListItem: (payload: HandleCallbackActionParams) => void,
  deleteTaskListItem: (payload: HandleCallbackActionParams) => void,
  duplicateTaskListItem: (payload: HandleCallbackActionParams) => void,
  toggleTaskListItemCompletion?: (payload: HandleCallbackActionParams) => void,
  updateTaskListItem: (payload: HandleCallbackActionParams) => void,
}

type ConfirmCompletionParams = {
  callbacks: ModalCallbacks,
  completed: boolean | undefined,
  toggleCompletionFn: UseTaskListItemPayload['callbacks']['toggleCompletion'],
}

const confirmCompletion = (params: ConfirmCompletionParams) => {
  const { callbacks, completed, toggleCompletionFn } = params
  const { closeModal, toggleTaskListItemCompletion } = callbacks

  const toggleTaskListItemCompletionPayload = {
    callbacks: {
      action: toggleCompletionFn,
      afterAction: closeModal,
    },
    toastText: 'Task Updated',
  }

  swal
    .fire({
      confirmButtonColor: '#43ac6a',
      confirmButtonText: completed ? 'Yes, mark as incomplete.' : 'Yes, complete it.',
      icon: 'success',
      showCancelButton: true,
      text: 'Are you sure?',
      title: 'Toggle Completion?',
    })
    .then(({ value }) => {
      if (value){
        // @ts-expect-error TS2722
        toggleTaskListItemCompletion(toggleTaskListItemCompletionPayload)
      }
    })
}

type ConfirmDeleteParams = {
  callbacks: ModalCallbacks,
  deleteFn: UseTaskListItemPayload['callbacks']['deleteTaskListItem'],
  taskList: TaskListModel,
}

const confirmDelete = (params: ConfirmDeleteParams) => {
  const { callbacks, deleteFn, taskList } = params
  const { closeModal, deleteTaskListItem } = callbacks

  const deleteTaskListItemPayload = {
    callbacks: {
      action: deleteFn,
      afterAction: closeModal,
    },
    taskList,
    toastText: 'Task Deleted',
  }

  swal
    .fire({
      confirmButtonColor: '#DD6B55',
      confirmButtonText: 'Yes, delete it.',
      icon: 'warning',
      showCancelButton: true,
      text: 'Are you sure?',
      title: 'Delete Task?',
    })
    .then(({ value }) => {
      if (value){
        // @ts-expect-error TS2345
        deleteTaskListItem(deleteTaskListItemPayload)
      }
    })
}

type ConfirmDuplicateParams = {
  callbacks: ModalCallbacks,
  duplicateFn: UseTaskListItemPayload['callbacks']['duplicateTaskListItem'],
  taskListItem: TaskListModel,
}

const confirmDuplicate = (params: ConfirmDuplicateParams) => {
  const { callbacks, duplicateFn, taskListItem } = params
  const { closeModal, duplicateTaskListItem } = callbacks

  const duplicateTaskListItemPayload = {
    callbacks: {
      action: duplicateFn,
      afterAction: closeModal,
    },
    entityParams: taskListItem,
    shouldRedirect: true,
    toastText: 'Task List Item Duplicated',
  }

  swal
    .fire({
      confirmButtonColor: '#43ac6a',
      confirmButtonText: 'Yes, duplicate it.',
      icon: 'success',
      showCancelButton: true,
      text: 'Are you sure?',
      title: 'Duplicate Task List Item?',
    })
    .then(({ value }) => {
      if (value){
        // @ts-expect-error TS2345
        duplicateTaskListItem(duplicateTaskListItemPayload)
      }
    })
}

type ModalProps = {
  callbacks: ModalCallbacks,
  requestOptions?: object,
  showModal: boolean,
  taskListItem?: Partial<TaskListItemModel>,
}

const CreateOrEditTaskListItemModal = (props: ModalProps) => {
  const { callbacks, showModal, taskListItem: initTaskListItem } = props
  const {
    closeModal, createTaskListItem, toggleTaskListItemCompletion, updateTaskListItem,
  } = callbacks || {}

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = modalData?.[MODAL_KEY] ?? {}
  const taskListItem = {
    ...initTaskListItem,
    ...modalPayload?.taskListItem ?? {},
  }

  const newRecord = !taskListItem.id
  const isSubTask = !!taskListItem.parent_task_id

  const taskListItemPayload = useTaskListItem(taskListItem)
  const {
    callbacks: {
      createTaskListItem: createFn,
      deleteTaskListItem: deleteFn,
      duplicateTaskListItem: duplicateFn,
      toggleCompletion: toggleCompletionFn,
      updateTaskListItem: updateFn,
    },
    creating,
    taskList,
    taskListItem: {
      completed,
    },
    updating,
  } = taskListItemPayload

  const {
    callbacks: {
      toggleRelatedEntityAttributes,
    },
    formPayload: {
      entityState,
      entityState: {
        assignee_id,
        complexity,
        description,
        due_date,
        notification_emails,
        parent_task_id,
        priority,
        status_id,
        task_list_id,
        task_list_section_id,
        task_type,
        template_page_type,
        ticketing_system_uid,
        title,
      },
      errors,
      handlers,
      saveEnabled,
      setEntityState,
    },
    providerRelatedEntity,
    serviceProductRelatedEntity,
    serviceProductTemplatePages,
    statuses,
    templatePageRelatedEntity,
  } = useCreateOrEditTaskListItemModal(taskListItem)

  const isConditionalTask = getTaskTypeSetting(task_type, 'conditional')

  const { isMasterAdmin } = useCurrentUser()

  const filteredTaskTypes = isSubTask ? subTaskTypes : taskTypes

  const createOrEditTaskListItemPayload = {
    callbacks: {
      action: newRecord ? createFn : updateFn,
      afterAction: closeModal,
    },
    entityParams: entityState,
    requestOptions: {
      include_related_entity_subject: true,
      include_task_list_item_related_entities: true,
    },
    toastText: `Task List Item ${newRecord ? 'Created' : 'Updated'}`,
  }

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={MODAL_KEY} showModal={showModal}>
      <SidebarModal.Header callbacks={callbacks} title={newRecord ? 'Create' : 'Edit'} titleSecondLine="Task" />

      <SidebarModal.Content>
        <Form>
          <EntitySelector
            // @ts-expect-error TS2741
            callbacks={{
              selectItem: taskList => setEntityState({ task_list_id: taskList?.id }),
            }}
            disabled={isSubTask}
            label="* Project"
            entityKey="taskLists"
            selectedItemId={task_list_id}
          />

          {!!task_list_id && (
            <EntitySelector
              // @ts-expect-error TS2741
              callbacks={{
                selectItem: taskListSection => setEntityState({ task_list_section_id: taskListSection?.id }),
              }}
              disabled={isSubTask}
              label="* Project Group"
              entityKey="taskListSections"
              marginTop="large"
              searchRequestOptions={{ task_list_id }}
              selectedItemId={task_list_section_id}
            />
          )}

          {isSubTask && (
            <EntitySelector
              // @ts-expect-error TS2741
              callbacks={{
                selectItem: taskListItem => setEntityState({ parent_task_id: taskListItem?.id }),
              }}
              disabled
              label="* Parent Task"
              entityKey="taskListItems"
              marginTop="large"
              searchRequestOptions={{ parent_task_id }}
              selectedItemId={parent_task_id}
            />
          )}

          <Form.Field label="* Task Type" marginTop="large">
            <select
              name="task_type"
              value={task_type}
              onChange={(e) => {
                const changes = {
                  task_type: e.target.value,
                }

                // @ts-expect-error TS2339
                if (isSubTask) changes.title = e.target.options[e.target.selectedIndex].text

                setEntityState(changes)
              }}
            >
              <option value="">Please Select...</option>
              {filteredTaskTypes.map(taskTypeKey => (
                <option key={taskTypeKey} value={taskTypeKey}>
                  {/*
                   // @ts-expect-error TS2345 */}
                  {getTaskTypeSetting(taskTypeKey, 'label')}
                </option>
              ))}
            </select>
          </Form.Field>

          {newRecord && !isSubTask && (
            <>
              <EntitySelector
                // @ts-expect-error TS2741
                callbacks={{
                  selectItem: provider => toggleRelatedEntityAttributes({
                    // @ts-expect-error TS2322
                    subjectId: provider?.id,
                    subjectType: 'Provider',
                  }),
                }}
                label="Provider"
                entityKey="providers"
                marginTop="large"
                selectedItemId={providerRelatedEntity?.subject_id}
              />

              <EntitySelector
                // @ts-expect-error TS2741
                callbacks={{
                  selectItem: (product) => {
                    setEntityState({ template_page_type: '', title: product?.text })
                    toggleRelatedEntityAttributes({
                      // @ts-expect-error TS2322
                      subjectId: product?.id,
                      subjectType: 'ServiceProduct',
                    })
                  },
                }}
                label="Product"
                entityKey="serviceProducts"
                marginTop="large"
                searchRequestOptions={{ provider_id: providerRelatedEntity?.subject_id }}
                selectedItemId={serviceProductRelatedEntity?.subject_id}
              />

              {!!serviceProductTemplatePages.length && (
                <Form.Field label="* Template Page Type" marginTop="large">
                  {/*
                   // @ts-expect-error TS2322 */}
                  <select name="template_page_type" value={template_page_type} {...handlers}>
                    <option value="">Please Select...</option>
                    {serviceProductTemplatePages.map((page: { title: string }) => {
                      const { title: pageTitle } = page

                      return (
                        <option key={pageTitle} value={pageTitle}>
                          {pageTitle}
                        </option>
                      )
                    })}
                  </select>
                </Form.Field>
              )}

              <EntitySelector
                // @ts-expect-error TS2741
                callbacks={{
                  getSearchEntities: entities => entities.map((entity) => {
                    const { id, title: entityTitle } = entity
                    return { ...entity, title: `#${id} - ${entityTitle}` }
                  }),
                  selectItem: templatePage => toggleRelatedEntityAttributes({
                    data: {
                      template_type: 'origin',
                    },
                    // @ts-expect-error TS2322
                    subjectId: templatePage?.id,
                    subjectType: 'TemplatePage',
                  }),
                }}
                label="Origin Template Page"
                entityKey="templatePages"
                marginTop="large"
                selectedItemId={templatePageRelatedEntity?.subject_id}
              />
            </>
          )}

          <Form.Field label="* Title" errorMessage={errors.title} marginTop="large">
            <input name="title" type="text" value={title} {...handlers} />
          </Form.Field>

          {/*
           // @ts-expect-error TS2339 */}
          {!!taskList.ad_hoc && (
            <Form.Field label="* Ticketing System ID" errorMessage={errors.ticketing_system_uid} marginTop="large">
              <input name="ticketing_system_uid" type="text" value={ticketing_system_uid} {...handlers} />
            </Form.Field>
          )}

          <Form.Field label="Description" errorMessage={errors.description} marginTop="large">
            {/*
             // @ts-expect-error TS2322 */}
            <textarea name="description" style={{ height: 80 }} value={description} {...handlers} />
          </Form.Field>

          {!isSubTask && (
            <>
              <Form.Field label="Status" errorMessage={errors.status_id} marginTop="large">
                {/*
                 // @ts-expect-error TS2322 */}
                <select name="status_id" value={status_id} {...handlers}>
                  <option value="">Default</option>
                  {statuses.map((status) => {
                    const { id, title: statusTitle } = status

                    return (
                      <option key={id} value={id}>
                        {statusTitle}
                      </option>
                    )
                  })}
                </select>
              </Form.Field>

              <Form.Field label="Priority" errorMessage={errors.priority} marginTop="large">
                {/*
                 // @ts-expect-error TS2322 */}
                <select name="priority" value={priority} {...handlers}>
                  <option value="">Please Select...</option>
                  {priorityKeys.map((priorityKey) => {
                    const { label } = prioritySettings[priorityKey]

                    return (
                      <option key={priorityKey} value={priorityKey}>
                        {label}
                      </option>
                    )
                  })}
                </select>
              </Form.Field>

              <Form.Field label="Complexity" marginTop="large">
                {/*
                 // @ts-expect-error TS2322 */}
                <select
                  data-value-type="number"
                  value={complexity}
                  name="complexity"
                  {...handlers}
                >
                  <option value={1}>Easy</option>
                  <option value={5}>Medium</option>
                  <option value={10}>Hard</option>
                </select>
              </Form.Field>

              <Form.Field label="Due Date" marginTop="large">
                <input name="due_date" type="date" value={due_date} {...handlers} />
              </Form.Field>
            </>
          )}

          <EntitySelector
            // @ts-expect-error TS2741
            callbacks={{
              getSearchEntities: entities => entities.map((entity) => {
                const { id, full_name } = entity
                return { ...entity, full_name: `#${id} - ${full_name}` }
              }),
              selectItem: user => setEntityState({ assignee_id: user?.id || '' }),
            }}
            label="Assignee"
            entityKey="users"
            entityTitleKey="full_name"
            marginTop="large"
            searchRequestOptions={{ role_keys: templateManagerRoleKeys }}
            selectedItemId={assignee_id}
            showEntityId
          />

          <EntitySelector
            // @ts-expect-error TS2741
            callbacks={{
              getSearchEntities: entities => entities.map((entity) => {
                const { id, full_name } = entity
                return { ...entity, full_name: `#${id} - ${full_name}` }
              }),
              selectItem: user => setEntityState({
                // @ts-expect-error TS18047
                notification_emails: toggleArray(notification_emails, user.email),
              }),
            }}
            label="Notification Recipients"
            entityKey="users"
            marginTop="large"
            searchRequestOptions={{ role_keys: templateManagerRoleKeys }}
            showEntityId
            showSelectedEntity={false}
          />

          {notification_emails.map((email: string) => (
            <SidebarModalListItem boxProps={{ paddingY: 'medium' }} key={email} title={email}>
              {/*
               // @ts-expect-error TS2339 */}
              <SidebarModalListItem.Actions>
                {/*
                 // @ts-expect-error TS2339 */}
                <SidebarModalListItem.Icon
                  marginRight="medium"
                  onClick={() => setEntityState({
                    notification_emails: toggleArray(notification_emails, email),
                  })}
                  type="delete"
                />
              {/*
               // @ts-expect-error TS2339 */}
              </SidebarModalListItem.Actions>
            </SidebarModalListItem>
          ))}

          {!newRecord && (
            <Form.Field label="More Options" marginTop="large">
              {!isConditionalTask && toggleTaskListItemCompletion && (
                <Button
                  buttonStyle="secondaryUtility"
                  icon={<FontAwesomeIcon icon={completed ? faTimes : faCheck} />}
                  onClick={() => confirmCompletion({ callbacks, completed, toggleCompletionFn })}
                  marginBottom="small"
                  size="medium"
                >
                  {completed ? 'Mark as Incomplete' : 'Complete Task'}
                </Button>
              )}

              {(!completed || isMasterAdmin) && (
                <Button
                  buttonStyle="secondaryUtility"
                  icon={<FontAwesomeIcon icon={faTrashAlt} />}
                  onClick={() => confirmDelete({
                    callbacks,
                    deleteFn,
                    taskList: isSubTask ? taskListItem : taskList,
                  })}
                  size="medium"
                >
                  Delete Task
                </Button>
              )}

              {!isSubTask && (
                <Button
                  buttonStyle="secondaryUtility"
                  icon={<FontAwesomeIcon icon={faCopy} />}
                  marginTop="small"
                  onClick={() => confirmDuplicate({
                    callbacks,
                    duplicateFn,
                    taskListItem,
                  })}
                  size="medium"
                >
                  Duplicate Task
                </Button>
              )}

            </Form.Field>
          )}
        </Form>
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          disabled={!saveEnabled}
          loading={creating || updating}
          loadingBubbleColor="white"
          onClick={
            newRecord
              // @ts-expect-error TS2345
              ? () => createTaskListItem(createOrEditTaskListItemPayload)
              // @ts-expect-error TS2345
              : () => updateTaskListItem(createOrEditTaskListItemPayload)
          }
          size="large"
        >
          {saveEnabled ? `${newRecord ? 'Create' : 'Update'} Task` : 'Complete Fields'}
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <CreateOrEditTaskListItemModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
